import React, { useState, useEffect } from 'react';
import { Search, Plus, Briefcase, DollarSign, TrendingUp, Users, Code, UserCheck, ChevronRight, X, UserPlus, Edit3, Trash2, Check } from 'lucide-react';
import { toast } from 'sonner';

// Use runtime window.location.origin for API URL to avoid build-time caching issues
const getApiUrl = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

// Icon mapping for department icons
const iconMap = {
  Briefcase,
  DollarSign,
  TrendingUp,
  Users,
  Code,
  UserCheck
};

const RealEstateDepartments = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDepartmentModal, setShowAddDepartmentModal] = useState(false);
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [showEditEmployeeModal, setShowEditEmployeeModal] = useState(false);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [editingDepartmentId, setEditingDepartmentId] = useState(null);
  const [editingDepartmentName, setEditingDepartmentName] = useState('');
  const [loading, setLoading] = useState(true);
  
  // New department form state
  const [newDepartment, setNewDepartment] = useState({
    name: '',
    iconColor: 'text-purple-500',
    iconBg: 'bg-purple-500/10'
  });
  
  // New employee form state
  const [newEmployee, setNewEmployee] = useState({
    name: '',
    role: '',
    phone: '',
    avatar: ''
  });

  // Edit employee form state
  const [editEmployee, setEditEmployee] = useState({
    name: '',
    role: '',
    phone: '',
    avatar: ''
  });

  const [departments, setDepartments] = useState([]);

  // Fetch departments from backend
  const fetchDepartments = async () => {
    const API_URL = getApiUrl();
    try {
      const response = await fetch(`${API_URL}/api/departments`);
      if (response.ok) {
        const data = await response.json();
        // Map icon names to actual icon components
        const deptWithIcons = data.map(dept => ({
          ...dept,
          icon: iconMap[dept.iconName] || Users
        }));
        setDepartments(deptWithIcons);
      } else {
        console.error('Failed to fetch departments:', response.status);
        toast.error('Failed to load departments');
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      toast.error('Failed to load departments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, []);

  // Start editing department name
  const startEditingDepartmentName = (dept) => {
    setEditingDepartmentId(dept.id);
    setEditingDepartmentName(dept.name);
  };

  // Save department name
  const saveDepartmentName = async (departmentId) => {
    if (!editingDepartmentName.trim()) {
      setEditingDepartmentId(null);
      return;
    }

    const API_URL = getApiUrl();
    try {
      const response = await fetch(`${API_URL}/api/departments/${departmentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editingDepartmentName })
      });

      if (response.ok) {
        setDepartments(departments.map(dept => 
          dept.id === departmentId ? { ...dept, name: editingDepartmentName } : dept
        ));
        toast.success('Department name updated');
      } else {
        toast.error('Failed to update department name');
      }
    } catch (error) {
      console.error('Error updating department:', error);
      toast.error('Failed to update department name');
    }
    
    setEditingDepartmentId(null);
  };

  // Delete department
  const deleteDepartment = async (departmentId) => {
    if (!window.confirm('Are you sure you want to delete this department and all its members?')) return;

    const API_URL = getApiUrl();
    try {
      const response = await fetch(`${API_URL}/api/departments/${departmentId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setDepartments(departments.filter(dept => dept.id !== departmentId));
        toast.success('Department deleted');
      } else {
        toast.error('Failed to delete department');
      }
    } catch (error) {
      console.error('Error deleting department:', error);
      toast.error('Failed to delete department');
    }
  };

  // Open edit modal for a member
  const openEditMemberModal = (departmentId, member) => {
    setSelectedDepartmentId(departmentId);
    setEditingMember(member);
    setEditEmployee({
      name: member.name,
      role: member.role,
      phone: member.phone,
      avatar: member.avatar
    });
    setShowEditEmployeeModal(true);
  };

  // Save edited member
  const handleSaveEditedMember = async () => {
    if (!editEmployee.name || !editEmployee.role) return;
    
    const API_URL = getApiUrl();
    try {
      const response = await fetch(`${API_URL}/api/departments/${selectedDepartmentId}/members/${editingMember.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editEmployee)
      });

      if (response.ok) {
        setDepartments(departments.map(dept => {
          if (dept.id === selectedDepartmentId) {
            return {
              ...dept,
              members: dept.members.map(member => 
                member.id === editingMember.id
                  ? { ...member, ...editEmployee }
                  : member
              )
            };
          }
          return dept;
        }));
        toast.success('Member updated');
      } else {
        toast.error('Failed to update member');
      }
    } catch (error) {
      console.error('Error updating member:', error);
      toast.error('Failed to update member');
    }
    
    setShowEditEmployeeModal(false);
    setEditingMember(null);
    setEditEmployee({ name: '', role: '', phone: '', avatar: '' });
  };

  // Delete member from department
  const handleDeleteMember = async (departmentId, memberId) => {
    if (!window.confirm('Are you sure you want to remove this member from the department?')) return;
    
    const API_URL = getApiUrl();
    try {
      const response = await fetch(`${API_URL}/api/departments/${departmentId}/members/${memberId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setDepartments(departments.map(dept => {
          if (dept.id === departmentId) {
            const updatedMembers = dept.members.filter(member => member.id !== memberId);
            return {
              ...dept,
              members: updatedMembers,
              memberCount: updatedMembers.length
            };
          }
          return dept;
        }));
        toast.success('Member removed');
      } else {
        toast.error('Failed to remove member');
      }
    } catch (error) {
      console.error('Error deleting member:', error);
      toast.error('Failed to remove member');
    }
  };

  // Add new department
  const handleAddDepartment = async () => {
    if (!newDepartment.name.trim()) return;

    const API_URL = getApiUrl();
    try {
      const response = await fetch(`${API_URL}/api/departments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newDepartment.name,
          iconName: 'Users',
          iconColor: newDepartment.iconColor,
          iconBg: newDepartment.iconBg
        })
      });

      if (response.ok) {
        const created = await response.json();
        setDepartments([...departments, { ...created, icon: Users }]);
        toast.success('Department created');
        setShowAddDepartmentModal(false);
        setNewDepartment({ name: '', iconColor: 'text-purple-500', iconBg: 'bg-purple-500/10' });
      } else {
        toast.error('Failed to create department');
      }
    } catch (error) {
      console.error('Error creating department:', error);
      toast.error('Failed to create department');
    }
  };

  // Add new employee
  const handleAddEmployee = async () => {
    if (!newEmployee.name || !newEmployee.role) return;

    const API_URL = getApiUrl();
    try {
      const response = await fetch(`${API_URL}/api/departments/${selectedDepartmentId}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newEmployee)
      });

      if (response.ok) {
        const created = await response.json();
        setDepartments(departments.map(dept => {
          if (dept.id === selectedDepartmentId) {
            return {
              ...dept,
              members: [...dept.members, created],
              memberCount: dept.memberCount + 1
            };
          }
          return dept;
        }));
        toast.success('Employee added');
        setShowAddEmployeeModal(false);
        setNewEmployee({ name: '', role: '', phone: '', avatar: '' });
      } else {
        toast.error('Failed to add employee');
      }
    } catch (error) {
      console.error('Error adding employee:', error);
      toast.error('Failed to add employee');
    }
  };

  const filteredDepartments = departments.map(dept => ({
    ...dept,
    members: dept.members.filter(member =>
      member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.role.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(dept => 
    dept.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    dept.members.length > 0
  );

  return (
    <div className="min-h-screen bg-black text-white p-3 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-2">
          <div>
            <h1 className="text-2xl md:text-4xl font-bold mb-1">Departments</h1>
            <p className="text-gray-400 text-xs md:text-sm">Manage and view all company departments and their members</p>
          </div>
          <button 
            onClick={() => setShowAddDepartmentModal(true)}
            className="flex items-center gap-1.5 md:gap-2 px-4 md:px-6 py-2 md:py-3 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium transition-all text-sm md:text-base"
          >
            <Plus size={18} />
            <span className="hidden sm:inline">Add Department</span>
            <span className="sm:hidden">Add Dept</span>
          </button>
        </div>

        {/* Search Bar */}
        <div className="relative mt-3 md:mt-4">
          <Search className="absolute left-3 md:left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search departments or members..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 md:pl-12 pr-4 py-2.5 md:py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-gray-600 text-sm"
          />
        </div>
      </div>

      {/* Departments Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {filteredDepartments.map((department) => (
          <div
            key={department.id}
            className="bg-black border-2 border-gray-400 rounded-lg p-4 md:p-6 hover:border-white hover:shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-all duration-300"
          >
            {/* Department Header */}
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-1">
                <div className={`${department.iconBg} p-2 md:p-3 rounded-lg flex-shrink-0`}>
                  <department.icon className={department.iconColor} size={20} />
                </div>
                <div className="min-w-0 flex-1">
                  {editingDepartmentId === department.id ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editingDepartmentName}
                        onChange={(e) => setEditingDepartmentName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveDepartmentName(department.id);
                          if (e.key === 'Escape') setEditingDepartmentId(null);
                        }}
                        className="bg-gray-800 border border-purple-500 rounded px-2 py-1 text-base md:text-xl font-bold w-full max-w-[200px] focus:outline-none focus:ring-2 focus:ring-purple-500"
                        autoFocus
                        data-testid={`edit-department-name-input-${department.id}`}
                      />
                      <button
                        onClick={() => saveDepartmentName(department.id)}
                        className="p-1.5 bg-green-600 hover:bg-green-500 rounded-lg transition-colors"
                        title="Save"
                        data-testid={`save-department-name-${department.id}`}
                      >
                        <Check size={14} className="text-white" />
                      </button>
                      <button
                        onClick={() => setEditingDepartmentId(null)}
                        className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                        title="Cancel"
                      >
                        <X size={14} className="text-white" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 group/title">
                      <h2 className="text-base md:text-xl font-bold truncate">{department.name}</h2>
                      <button
                        onClick={() => startEditingDepartmentName(department)}
                        className="p-1 hover:bg-gray-800 rounded opacity-0 group-hover/title:opacity-100 transition-all"
                        title="Edit department name"
                        data-testid={`edit-department-name-btn-${department.id}`}
                      >
                        <Edit3 size={14} className="text-gray-400 hover:text-white" />
                      </button>
                    </div>
                  )}
                  <p className="text-gray-400 text-xs md:text-sm">{department.memberCount} members</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
                <button 
                  onClick={() => openAddEmployeeModal(department.id)}
                  className="flex items-center gap-1 px-2 md:px-3 py-1 md:py-1.5 bg-green-600 hover:bg-green-700 rounded-lg text-xs md:text-sm font-medium transition-all"
                >
                  <UserPlus size={12} />
                  <span className="hidden sm:inline">Add</span>
                </button>
                <button className="text-purple-400 hover:text-purple-300 text-xs md:text-sm font-medium whitespace-nowrap">
                  See All
                </button>
              </div>
            </div>

            {/* Members List */}
            <div className="space-y-2 md:space-y-3">
              {department.members.length === 0 ? (
                <div className="text-center py-6 md:py-8 border border-dashed border-gray-600 rounded-lg">
                  <Users size={28} className="mx-auto text-gray-600 mb-2" />
                  <p className="text-gray-400 text-xs md:text-sm mb-2">No members yet</p>
                  <button 
                    onClick={() => openAddEmployeeModal(department.id)}
                    className="text-purple-400 hover:text-purple-300 text-xs md:text-sm"
                  >
                    Add the first employee →
                  </button>
                </div>
              ) : (
                department.members.slice(0, 4).map((member) => (
                <div
                  key={member.id}
                  className="bg-black border border-gray-400 rounded-lg p-3 md:p-4 flex items-center justify-between hover:border-white hover:shadow-[0_0_15px_rgba(255,255,255,0.3)] transition-all duration-300 group"
                  data-testid={`member-row-${department.id}-${member.id}`}
                >
                  <div className="flex items-center gap-2 md:gap-3 min-w-0">
                    <img
                      src={member.avatar}
                      alt={member.name}
                      className="w-10 md:w-12 h-10 md:h-12 rounded-full object-cover flex-shrink-0"
                    />
                    <div className="min-w-0">
                      <h3 className="font-semibold text-sm md:text-base truncate">{member.name}</h3>
                      <p className="text-gray-400 text-xs md:text-sm truncate">{member.role}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
                    <span className="text-gray-400 text-xs md:text-sm hidden sm:inline mr-2">{member.phone}</span>
                    {/* Edit/Delete buttons - show on hover */}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditMemberModal(department.id, member);
                        }}
                        className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors"
                        title="Edit member"
                        data-testid={`edit-member-${member.id}`}
                      >
                        <Edit3 size={14} className="text-gray-400 hover:text-white" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteMember(department.id, member.id);
                        }}
                        className="p-1.5 hover:bg-red-500/20 rounded-lg transition-colors"
                        title="Remove member"
                        data-testid={`delete-member-${member.id}`}
                      >
                        <Trash2 size={14} className="text-gray-400 hover:text-red-400" />
                      </button>
                    </div>
                    <ChevronRight size={14} className="text-gray-400" />
                  </div>
                </div>
              ))
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add Department Modal */}
      {showAddDepartmentModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-2 md:p-4" onClick={() => setShowAddDepartmentModal(false)}>
          <div className="bg-black border border-gray-400 rounded-xl w-full max-w-md overflow-hidden hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-400/20 flex items-center justify-between">
              <h3 className="text-base md:text-lg font-bold text-white">Add New Department</h3>
              <button 
                onClick={() => setShowAddDepartmentModal(false)}
                className="p-1.5 md:p-2 hover:bg-gray-900 rounded-full transition-colors"
              >
                <X size={18} className="text-gray-400" />
              </button>
            </div>
            
            <div className="p-4 md:p-6 space-y-3 md:space-y-4">
              {/* Department Name */}
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-300 mb-1.5 md:mb-2">Department Name</label>
                <input
                  type="text"
                  value={newDepartment.name}
                  onChange={(e) => setNewDepartment(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Engineering, Marketing..."
                  className="w-full px-3 md:px-4 py-2 md:py-2.5 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
              </div>
              
              {/* Icon Selection */}
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-300 mb-1.5 md:mb-2">Department Icon</label>
                <div className="grid grid-cols-6 gap-1.5 md:gap-2">
                  {iconOptions.map((option, idx) => (
                    <button
                      key={idx}
                      onClick={() => setNewDepartment(prev => ({ ...prev, iconColor: option.color, iconBg: option.bg }))}
                      className={`p-2 md:p-3 rounded-lg border transition-all ${
                        newDepartment.iconColor === option.color 
                          ? 'border-purple-500 bg-purple-500/20' 
                          : 'border-gray-700 hover:border-gray-500'
                      }`}
                    >
                      <option.icon className={option.color} size={18} />
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="px-4 md:px-6 py-3 md:py-4 border-t border-gray-400/20 flex items-center justify-end gap-2 md:gap-3">
              <button 
                onClick={() => setShowAddDepartmentModal(false)}
                className="px-3 md:px-4 py-1.5 md:py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-xs md:text-sm font-medium"
              >
                Cancel
              </button>
              <button 
                onClick={handleAddDepartment}
                disabled={!newDepartment.name.trim()}
                className="px-3 md:px-4 py-1.5 md:py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs md:text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Department
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Employee Modal */}
      {showAddEmployeeModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-2 md:p-4" onClick={() => setShowAddEmployeeModal(false)}>
          <div className="bg-black border border-gray-400 rounded-xl w-full max-w-md overflow-hidden hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-400/20 flex items-center justify-between">
              <div>
                <h3 className="text-base md:text-lg font-bold text-white">Add New Employee</h3>
                <p className="text-[10px] md:text-xs text-gray-400">
                  Adding to: {departments.find(d => d.id === selectedDepartmentId)?.name}
                </p>
              </div>
              <button 
                onClick={() => setShowAddEmployeeModal(false)}
                className="p-1.5 md:p-2 hover:bg-gray-900 rounded-full transition-colors"
              >
                <X size={18} className="text-gray-400" />
              </button>
            </div>
            
            <div className="p-4 md:p-6 space-y-3 md:space-y-4">
              {/* Employee Name */}
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-300 mb-1.5 md:mb-2">Full Name *</label>
                <input
                  type="text"
                  value={newEmployee.name}
                  onChange={(e) => setNewEmployee(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Doe"
                  className="w-full px-3 md:px-4 py-2 md:py-2.5 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
              </div>
              
              {/* Role */}
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-300 mb-1.5 md:mb-2">Role / Position *</label>
                <input
                  type="text"
                  value={newEmployee.role}
                  onChange={(e) => setNewEmployee(prev => ({ ...prev, role: e.target.value }))}
                  placeholder="Software Engineer"
                  className="w-full px-3 md:px-4 py-2 md:py-2.5 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
              </div>
              
              {/* Phone */}
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-300 mb-1.5 md:mb-2">Phone Number</label>
                <input
                  type="text"
                  value={newEmployee.phone}
                  onChange={(e) => setNewEmployee(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="(555) 123-4567"
                  className="w-full px-3 md:px-4 py-2 md:py-2.5 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
              </div>
              
              {/* Avatar URL */}
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-300 mb-1.5 md:mb-2">Photo URL (optional)</label>
                <input
                  type="text"
                  value={newEmployee.avatar}
                  onChange={(e) => setNewEmployee(prev => ({ ...prev, avatar: e.target.value }))}
                  placeholder="https://example.com/photo.jpg"
                  className="w-full px-3 md:px-4 py-2 md:py-2.5 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
                <p className="text-[10px] md:text-xs text-gray-500 mt-1">Leave empty to auto-generate avatar</p>
              </div>
            </div>
            
            <div className="px-4 md:px-6 py-3 md:py-4 border-t border-gray-400/20 flex items-center justify-end gap-2 md:gap-3">
              <button 
                onClick={() => setShowAddEmployeeModal(false)}
                className="px-3 md:px-4 py-1.5 md:py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-xs md:text-sm font-medium"
              >
                Cancel
              </button>
              <button 
                onClick={handleAddEmployee}
                disabled={!newEmployee.name.trim() || !newEmployee.role.trim()}
                className="px-3 md:px-4 py-1.5 md:py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs md:text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Employee
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Employee Modal */}
      {showEditEmployeeModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-2 md:p-4" onClick={() => setShowEditEmployeeModal(false)}>
          <div className="bg-black border border-gray-400 rounded-xl w-full max-w-md overflow-hidden hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-400/20 flex items-center justify-between">
              <h3 className="text-base md:text-lg font-bold text-white">Edit Employee</h3>
              <button 
                onClick={() => setShowEditEmployeeModal(false)}
                className="p-1.5 md:p-2 hover:bg-gray-900 rounded-full transition-colors"
              >
                <X size={18} className="text-gray-400" />
              </button>
            </div>
            
            <div className="p-4 md:p-6 space-y-3 md:space-y-4">
              {/* Employee Name */}
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-300 mb-1.5 md:mb-2">Full Name</label>
                <input
                  type="text"
                  value={editEmployee.name}
                  onChange={(e) => setEditEmployee(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Employee name"
                  className="w-full px-3 md:px-4 py-2 md:py-2.5 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                  data-testid="edit-employee-name"
                />
              </div>
              
              {/* Role */}
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-300 mb-1.5 md:mb-2">Role / Title</label>
                <input
                  type="text"
                  value={editEmployee.role}
                  onChange={(e) => setEditEmployee(prev => ({ ...prev, role: e.target.value }))}
                  placeholder="e.g., Senior Developer"
                  className="w-full px-3 md:px-4 py-2 md:py-2.5 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                  data-testid="edit-employee-role"
                />
              </div>
              
              {/* Phone */}
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-300 mb-1.5 md:mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={editEmployee.phone}
                  onChange={(e) => setEditEmployee(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="(555) 123-4567"
                  className="w-full px-3 md:px-4 py-2 md:py-2.5 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                  data-testid="edit-employee-phone"
                />
              </div>
              
              {/* Avatar URL */}
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-300 mb-1.5 md:mb-2">Profile Photo URL</label>
                <input
                  type="url"
                  value={editEmployee.avatar}
                  onChange={(e) => setEditEmployee(prev => ({ ...prev, avatar: e.target.value }))}
                  placeholder="https://example.com/photo.jpg"
                  className="w-full px-3 md:px-4 py-2 md:py-2.5 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                  data-testid="edit-employee-avatar"
                />
                {editEmployee.avatar && (
                  <div className="mt-2 flex items-center gap-2">
                    <img 
                      src={editEmployee.avatar} 
                      alt="Preview" 
                      className="w-10 h-10 rounded-full object-cover"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                    <span className="text-xs text-gray-400">Photo preview</span>
                  </div>
                )}
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowEditEmployeeModal(false)}
                  className="flex-1 px-4 py-2.5 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEditedMember}
                  disabled={!editEmployee.name || !editEmployee.role}
                  className="flex-1 px-4 py-2.5 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="save-edit-employee"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RealEstateDepartments;
